import axios from 'axios';

const BASE_URL = 'https://loyalty.bom.asia';
const API_BASE_URL = `${BASE_URL}/api/miniApp/quiz/templates`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const appQuizApi = {
  // Get all quiz templates with filters
  getAllQuizTemplates: async (params = {}) => {
    try {
      
      // Build query string
      const queryParams = new URLSearchParams();
      
      // Pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Filters
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/?${queryString}` : '/';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting all quiz templates:', error);
      throw error;
    }
  },

  // Get quiz template by ID (admin endpoint with full details)
  getQuizTemplateById: async (id) => {
    try {
      const response = await api.get(`/admin/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting quiz template by ID (admin):', error);
      throw error;
    }
  },

  // Create quiz template with thumbnail and questions
  createQuizTemplate: async (formData) => {
    try {
      
      // Create FormData for file upload
      const uploadFormData = new FormData();
      
      // Add thumbnail file (API expects 'quizThumbnail')
      if (formData.thumbnail) {
        uploadFormData.append('quizThumbnail', formData.thumbnail);
      }
      
      // Add other fields
      if (formData.name) uploadFormData.append('name', formData.name);
      if (formData.isActive !== undefined) uploadFormData.append('isActive', formData.isActive);
      if (formData.displayQuestions) uploadFormData.append('displayQuestions', formData.displayQuestions);
      
      // Add questions as JSON string (without media files)
      if (formData.questions) {
        const questionsWithoutMedia = formData.questions.map(q => ({
          content: q.content,
          type: q.type,
          timeLimit: q.timeLimit || 30,
          answers: q.answers.map(a => ({
            content: a.text,
            isCorrect: a.isCorrect
          }))
        }));
        uploadFormData.append('questions', JSON.stringify(questionsWithoutMedia));
      }
      
      // Add question media files
      if (formData.questionMedias && Array.isArray(formData.questionMedias)) {
        formData.questionMedias.forEach((file, index) => {
          if (file) {
            uploadFormData.append('questionMedias', file);
          }
        });
      }
      
      const response = await api.post('/', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Error creating quiz template:', error);
      throw error;
    }
  },

  // Update quiz template
  updateQuizTemplate: async (id, updateData) => {
    try {
      
      // Check if updating with new files (thumbnail or questionMedias) OR questions
      const hasFiles = updateData.thumbnail || (updateData.questionMedias && updateData.questionMedias.length > 0);
      const hasQuestions = updateData.questions && updateData.questions.length > 0;
      
      if (hasFiles || hasQuestions) {
        // Use FormData for file upload OR when updating questions
        const uploadFormData = new FormData();
        
        if (updateData.thumbnail) uploadFormData.append('quizThumbnail', updateData.thumbnail);
        if (updateData.name) uploadFormData.append('name', updateData.name);
        if (updateData.displayQuestions) uploadFormData.append('displayQuestions', updateData.displayQuestions);
        if (updateData.isActive !== undefined) uploadFormData.append('isActive', updateData.isActive);
        
        // Add questions as JSON string (without media files)
        if (updateData.questions) {
          // Handle both full question objects and partial updates (only id + timeLimit)
          const questionsWithoutMedia = updateData.questions.map(q => {
            // If question has id and timeLimit only (partial update)
            if (q.id && q.timeLimit !== undefined && !q.content) {
              return {
                id: q.id,
                timeLimit: q.timeLimit
              };
            }
            // Full question object
            return {
              id: q.id, // Include id if exists (for updates)
              content: q.content,
              type: q.type,
              timeLimit: q.timeLimit || 30,
              answers: q.answers ? q.answers.map(a => ({
                content: a.text || a.content,
                isCorrect: a.isCorrect
              })) : undefined
            };
          });
          
          uploadFormData.append('questions', JSON.stringify(questionsWithoutMedia));
        }
        
        // Add question media files
        if (updateData.questionMedias && Array.isArray(updateData.questionMedias)) {
          updateData.questionMedias.forEach((file, index) => {
            if (file) {
              uploadFormData.append('questionMedias', file);
            }
          });
        }
        
        const response = await api.put(`/${id}`, uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        return response.data;
      } else {
        // Regular JSON update (no file, no questions)
        const response = await api.put(`/${id}`, updateData);
        return response.data;
      }
    } catch (error) {
      console.error('❌ Error updating quiz template:', error);
      throw error;
    }
  },

  // Toggle quiz template active status (PATCH endpoint)
  toggleQuizTemplateActive: async (id) => {
    try {
      const response = await api.patch(`/${id}/toggle`);
      return response.data;
    } catch (error) {
      console.error('❌ Error toggling quiz template status:', error);
      throw error;
    }
  },

  // Delete quiz template
  deleteQuizTemplate: async (id) => {
    try {
      const response = await api.delete(`/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting quiz template:', error);
      throw error;
    }
  },

  // Helper: Get active quiz templates
  getActiveQuizTemplates: async () => {
    try {
      const response = await api.get('/?isActive=true');
      return response.data;
    } catch (error) {
      console.error('❌ Error getting active quiz templates:', error);
      throw error;
    }
  },

  // Helper: Get inactive quiz templates
  getInactiveQuizTemplates: async () => {
    try {
      const response = await api.get('/?isActive=false');
      return response.data;
    } catch (error) {
      console.error('❌ Error getting inactive quiz templates:', error);
      throw error;
    }
  },

  // Import quiz template from Excel file
  importQuizFromExcel: async (formData) => {
    try {
      // Create FormData for file upload
      const uploadFormData = new FormData();
      
      // Add Excel file (API expects 'excelFile')
      if (formData.excelFile) {
        uploadFormData.append('excelFile', formData.excelFile);
      }
      
      // Add thumbnail file (optional)
      if (formData.thumbnail) {
        uploadFormData.append('thumbnail', formData.thumbnail);
      }
      
      // Add other fields
      if (formData.name) uploadFormData.append('name', formData.name);
      if (formData.displayQuestions) uploadFormData.append('displayQuestions', formData.displayQuestions);
      if (formData.isActive !== undefined) uploadFormData.append('isActive', formData.isActive);
      if (formData.timeLimit) uploadFormData.append('timeLimit', formData.timeLimit);
      if (formData.defaultThumbnailUrl) uploadFormData.append('defaultThumbnailUrl', formData.defaultThumbnailUrl);
      
      const response = await api.post('/import-excel', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Error importing quiz from Excel:', error);
      throw error;
    }
  }
};

export default appQuizApi;

